import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Timer, CreditCard, Wallet, Banknote, QrCode, Loader2, MapPin, Clock, CalendarDays, X, CheckCircle2 } from "lucide-react";

export default function PaymentPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [slots, setSlots] = useState([]);
  const [venue, setVenue] = useState(null);
  const [spaceName, setSpaceName] = useState("");
  const [loading, setLoading] = useState(true);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [paymentMode, setPaymentMode] = useState("ONLINE");
  const [timer, setTimer] = useState(900); // 15 minutes

  // ePlayment
  const [eplaymentChannel, setEplaymentChannel] = useState("");
  const [eplaymentChannels, setEplaymentChannels] = useState([]);
  const [walletBalance, setWalletBalance] = useState(null);
  const [feeInfo, setFeeInfo] = useState(null);

  useEffect(() => {
    try {
      const raw = searchParams.get("slots");
      const parsed = raw ? JSON.parse(raw) : [];
      setSlots(parsed);
      const vId = searchParams.get("venue_id");
      const sName = searchParams.get("space_name") || "";
      setSpaceName(sName);
      if (vId) {
        api.get(`/venues/${vId}`).then(r => setVenue(r.data)).catch(() => {});
      }
    } catch { toast.error("Invalid booking data"); }
    setLoading(false);

    // Fetch wallet & channels
    api.get("/wallet/balance").then(r => setWalletBalance(r.data)).catch(() => {});
    api.get("/eplayment/channels").then(r => {
      const chs = r.data.channels || [];
      setEplaymentChannels(chs);
      const first = chs.find(c => c.available);
      if (first) setEplaymentChannel(first.code);
    }).catch(() => {});
  }, [searchParams]);

  // Timer countdown
  useEffect(() => {
    if (timer <= 0) {
      toast.error("Time's up! Your slot reservation has expired.");
      navigate(-1);
      return;
    }
    const interval = setInterval(() => setTimer(t => t - 1), 1000);
    return () => clearInterval(interval);
  }, [timer, navigate]);

  // Fee calc
  useEffect(() => {
    if (!eplaymentChannel || paymentMode !== "EPLAYMENT") { setFeeInfo(null); return; }
    api.get("/eplayment/calculate-fee", {
      params: { channel_code: eplaymentChannel, amount: totalAmount }
    }).then(r => setFeeInfo(r.data)).catch(() => setFeeInfo(null));
  }, [eplaymentChannel, paymentMode]);

  const totalAmount = slots.reduce((sum, s) => sum + (s.rate || 0), 0);
  const timerMin = Math.floor(timer / 60);
  const timerSec = timer % 60;
  const isTimerCritical = timer < 120;

  const handlePay = async () => {
    if (slots.length === 0) return;
    setBookingLoading(true);
    const spaceType = searchParams.get("space_type") || "resource";
    const spaceId = searchParams.get("space_id") || "";
    const date = searchParams.get("date") || "";
    const bookingIds = [];

    try {
      for (const slot of slots) {
        if (spaceType === "resource") {
          // Legacy resource booking via lock+confirm
          const lockRes = await api.post("/player/lock-slot", {
            resource_id: spaceId,
            start_dt: slot.start,
            end_dt: slot.end,
            hours: 1,
          });
          const bid = lockRes.data.booking_id;
          bookingIds.push(bid);

          if (paymentMode === "ONLINE") {
            // Set booking to PENDING_PAYMENT, then redirect to Stripe Checkout
            await api.post(`/player/confirm-booking/${bid}`, { payment_mode: "ONLINE" });
            const stripeRes = await api.post("/payments/checkout", {
              booking_id: bid,
              origin_url: window.location.origin,
            });
            if (stripeRes.data.url) {
              window.location.href = stripeRes.data.url;
              return;
            }
          } else if (paymentMode === "EPLAYMENT") {
            await api.post(`/player/confirm-booking/${bid}`, { payment_mode: "ONLINE" });
            const payRes = await api.post("/eplayment/pay", {
              booking_id: bid, channel_code: eplaymentChannel, origin_url: window.location.origin,
            });
            if (payRes.data.payment_url) {
              window.location.href = payRes.data.payment_url;
              return;
            }
          } else if (paymentMode === "WALLET") {
            await api.post(`/player/confirm-booking/${bid}`, { payment_mode: "WALLET" });
            await api.post("/wallet/pay", { amount: slot.rate, booking_id: bid, type: "cash" });
          } else if (paymentMode === "VENUE_DEPOSIT") {
            await api.post(`/player/confirm-booking/${bid}`, { payment_mode: "VENUE_DEPOSIT" });
            await api.post("/wallet/pay", { amount: slot.rate, booking_id: bid, type: "deposit" });
          }
        } else {
          // Space Engine booking
          const seRes = await api.post("/space-engine/book", {
            physical_space_id: spaceId,
            activity_configuration_id: slot.activity_configuration_id,
            start_time: slot.start,
            end_time: slot.end,
            payment_mode: paymentMode === "EPLAYMENT" ? "ONLINE" : paymentMode,
          });

          if (paymentMode === "ONLINE") {
            // Stripe checkout — SE returns a booking_id for Stripe processing
            const bid = seRes.data.booking_id;
            if (bid) {
              const stripeRes = await api.post("/payments/checkout", {
                booking_id: bid,
                origin_url: window.location.origin,
              });
              if (stripeRes.data.url) {
                window.location.href = stripeRes.data.url;
                return;
              }
            }
          } else if (paymentMode === "EPLAYMENT") {
            const bid = seRes.data.booking_id;
            if (bid) {
              const payRes = await api.post("/eplayment/pay", {
                booking_id: bid, channel_code: eplaymentChannel, origin_url: window.location.origin,
              });
              if (payRes.data.payment_url) {
                window.location.href = payRes.data.payment_url;
                return;
              }
            }
          }

          bookingIds.push(seRes.data.booking_reservation_id || slot.start);
        }
      }

      // Navigate to receipt with payment mode
      navigate(`/booking-receipt?ids=${bookingIds.join(",")}&venue=${encodeURIComponent(venue?.name || "")}&space=${encodeURIComponent(spaceName)}&total=${totalAmount}&count=${slots.length}&date=${date}&pm=${paymentMode}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Payment failed. Please try again.");
    }
    setBookingLoading(false);
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      {/* Timer */}
      <div className={`text-center p-4 rounded-sm mb-6 ${isTimerCritical ? "bg-red-50 border-2 border-red-200" : "bg-primary/5 border-2 border-primary/20"}`} data-testid="payment-timer">
        <Timer className={`w-6 h-6 mx-auto mb-1 ${isTimerCritical ? "text-red-500 animate-pulse" : "text-primary"}`} />
        <p className="font-heading text-3xl font-bold" data-testid="payment-timer-value">{timerMin}:{timerSec.toString().padStart(2, "0")}</p>
        <p className="text-xs text-muted-foreground">Complete payment before time runs out</p>
      </div>

      {/* Booking Summary */}
      <div className="border-2 border-slate-200 rounded-sm p-4 mb-6" data-testid="payment-summary">
        <h2 className="font-heading text-lg uppercase tracking-tight font-bold mb-3">Booking Summary</h2>
        {venue && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
            <MapPin className="w-3 h-3" /><span>{venue.name}</span>
          </div>
        )}
        {spaceName && (
          <p className="text-sm text-muted-foreground mb-3">Space: <span className="font-medium text-foreground">{spaceName}</span></p>
        )}
        <div className="space-y-2">
          {slots.map((slot, i) => (
            <div key={i} className="flex items-center justify-between p-2 bg-muted/30 rounded-sm" data-testid={`payment-slot-${i}`}>
              <div className="flex items-center gap-3">
                <div className="text-center">
                  <CalendarDays className="w-4 h-4 text-muted-foreground mx-auto" />
                  <p className="text-[10px] text-muted-foreground">{slot.start?.slice(0, 10)}</p>
                </div>
                <div>
                  <p className="text-sm font-bold font-heading">{slot.start?.slice(11, 16)} - {slot.end?.slice(11, 16)}</p>
                  {slot.activity_name && <p className="text-xs text-muted-foreground capitalize">{slot.activity_name}</p>}
                </div>
              </div>
              <span className="font-heading font-bold">{"\u20B1"}{(slot.rate || 0).toLocaleString()}</span>
            </div>
          ))}
        </div>
        <Separator className="my-3" />
        <div className="flex justify-between font-heading text-lg font-bold">
          <span>Total</span>
          <span className="text-primary" data-testid="payment-total">{"\u20B1"}{totalAmount.toLocaleString()}</span>
        </div>
      </div>

      {/* Payment Method Selection */}
      <div className="mb-6">
        <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-3">Choose Payment Method</h3>
        <div className="space-y-2">
          <button data-testid="pm-eplayment" onClick={() => setPaymentMode("EPLAYMENT")}
            className={`w-full flex items-center gap-3 p-3 border-2 rounded-sm text-left transition-colors ${paymentMode === "EPLAYMENT" ? "border-orange-500 bg-orange-50" : "border-slate-200"}`}>
            <QrCode className={`w-5 h-5 ${paymentMode === "EPLAYMENT" ? "text-orange-500" : "text-muted-foreground"}`} />
            <div className="flex-1">
              <p className="text-sm font-medium">ePlayment</p>
              <p className="text-xs text-muted-foreground">{eplaymentChannels.filter(c => c.available).map(c => c.name).join(", ") || "GCash, PayMaya, Card"}</p>
            </div>
          </button>

          {paymentMode === "EPLAYMENT" && eplaymentChannels.length > 0 && (
            <div className="ml-8 space-y-1 pb-1">
              {eplaymentChannels.filter(c => c.available).map(ch => (
                <button key={ch.code} onClick={() => setEplaymentChannel(ch.code)}
                  data-testid={`pm-channel-${ch.code}`}
                  className={`w-full flex items-center gap-2.5 p-2 rounded-sm text-left text-sm ${eplaymentChannel === ch.code ? "bg-orange-100 text-orange-700 font-medium" : "hover:bg-muted/50"}`}>
                  <img src={ch.logo} alt={ch.name} className="w-5 h-5 rounded object-contain" />
                  <span className="flex-1">{ch.name}</span>
                  {eplaymentChannel === ch.code && <div className="w-2 h-2 rounded-full bg-orange-500" />}
                </button>
              ))}
              {feeInfo && (
                <div className="mt-2 p-2 rounded-sm bg-muted/50 text-xs space-y-0.5">
                  <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span>{"\u20B1"}{feeInfo.subtotal_amount?.toLocaleString()}</span></div>
                  {feeInfo.service_fee > 0 && <div className="flex justify-between"><span className="text-muted-foreground">Service Fee</span><span>{"\u20B1"}{feeInfo.service_fee?.toLocaleString()}</span></div>}
                  <div className="flex justify-between font-bold pt-0.5 border-t"><span>Total</span><span>{"\u20B1"}{feeInfo.total_amount?.toLocaleString()}</span></div>
                </div>
              )}
            </div>
          )}

          <button data-testid="pm-online" onClick={() => setPaymentMode("ONLINE")}
            className={`w-full flex items-center gap-3 p-3 border-2 rounded-sm text-left transition-colors ${paymentMode === "ONLINE" ? "border-orange-500 bg-orange-50" : "border-slate-200"}`}>
            <CreditCard className={`w-5 h-5 ${paymentMode === "ONLINE" ? "text-orange-500" : "text-muted-foreground"}`} />
            <div className="flex-1">
              <p className="text-sm font-medium">Stripe Checkout</p>
              <p className="text-xs text-muted-foreground">International cards</p>
            </div>
          </button>

          <button data-testid="pm-deposit" onClick={() => { if ((walletBalance?.store_deposit || 0) >= totalAmount) setPaymentMode("VENUE_DEPOSIT"); else toast.error("Insufficient store deposit"); }}
            className={`w-full flex items-center gap-3 p-3 border-2 rounded-sm text-left transition-colors ${paymentMode === "VENUE_DEPOSIT" ? "border-orange-500 bg-orange-50" : "border-slate-200"} ${(walletBalance?.store_deposit || 0) < totalAmount ? "opacity-50" : ""}`}>
            <Banknote className={`w-5 h-5 ${paymentMode === "VENUE_DEPOSIT" ? "text-orange-500" : "text-muted-foreground"}`} />
            <div className="flex-1">
              <p className="text-sm font-medium">Pay at Venue</p>
              <p className="text-xs text-muted-foreground">Store deposit: {"\u20B1"}{(walletBalance?.store_deposit || 0).toLocaleString()}</p>
            </div>
          </button>

          <button data-testid="pm-wallet" onClick={() => { if ((walletBalance?.cash_balance || 0) >= totalAmount) setPaymentMode("WALLET"); else toast.error("Insufficient wallet balance"); }}
            className={`w-full flex items-center gap-3 p-3 border-2 rounded-sm text-left transition-colors ${paymentMode === "WALLET" ? "border-orange-500 bg-orange-50" : "border-slate-200"} ${(walletBalance?.cash_balance || 0) < totalAmount ? "opacity-50" : ""}`}>
            <Wallet className={`w-5 h-5 ${paymentMode === "WALLET" ? "text-orange-500" : "text-muted-foreground"}`} />
            <div className="flex-1">
              <p className="text-sm font-medium">SetPlay Wallet / $Play</p>
              <p className="text-xs text-muted-foreground">Balance: {"\u20B1"}{(walletBalance?.cash_balance || 0).toLocaleString()}</p>
            </div>
          </button>
        </div>
      </div>

      {/* Pay Button */}
      <Button data-testid="pay-now-btn" onClick={handlePay} disabled={bookingLoading || timer <= 0}
        className="w-full bg-orange-500 hover:bg-orange-600 text-white rounded-full font-heading uppercase tracking-wide h-14 text-base shadow-[3px_3px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-x-[3px] active:translate-y-[3px]">
        {bookingLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : `Pay \u20B1${totalAmount.toLocaleString()} Now`}
      </Button>

      <button onClick={() => navigate(-1)} className="w-full text-center text-sm text-muted-foreground mt-3 hover:text-foreground">
        Cancel & Go Back
      </button>
    </div>
  );
}
